import { createMemo, createRoot, getOwner, onCleanup, runWithOwner } from "solid-js"
import { createStore } from "solid-js/store"
import { DevicePreference, persisted } from "@/storage/persist"

export type NewChatBehavior = "new-chat" | "first-fork"

/** How many chats the sidebar's Working list shows until the user picks another number. */
const DEFAULT_WORKING_LIMIT = 8

/** A Working list size: a whole number of at least 1. */
export function isWorkingLimit(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 1
}

export interface NotificationSettings {
  agent: boolean
  permissions: boolean
  errors: boolean
}

// Each independently editable preference owns one storage key, so edits from
// different tabs compose instead of overwriting one shared record.
export function createDeviceSettings() {
  const owner = getOwner()
  if (!owner) throw new Error("Device settings require a reactive owner")

  function entry<T>(key: string) {
    const [store, setStore] = persisted(
      DevicePreference.global(`settings.v4:${key}`),
      createStore<{ value?: T }>({ value: undefined }),
    )
    return { value: () => store.value, set: (value: T) => setStore({ value }) }
  }

  function notification(key: keyof NotificationSettings) {
    const state = entry<boolean>(`notification:${key}`)
    return { value: () => state.value() ?? true, set: state.set }
  }
  const agent = notification("agent")
  const permissions = notification("permissions")
  const errors = notification("errors")
  const newChatBehavior = entry<NewChatBehavior>("new-chat:behavior")
  const shareTyping = entry<boolean>("sharing:show-typing")
  const workingLimit = entry<number>("sidebar:working-limit")

  function createKeybind(action: string) {
    const state = entry<string | null>(`keybind:${encodeURIComponent(action)}`)
    return {
      value: createMemo(() => state.value() ?? undefined),
      set: state.set,
    }
  }
  const keybinds = new Map<string, ReturnType<typeof createKeybind>>()
  onCleanup(() => keybinds.clear())
  function keybind(action: string) {
    const existing = keybinds.get(action)
    if (existing) return existing
    // Command reads may happen inside short-lived computations or event handlers;
    // each lazily loaded entry belongs to the settings provider instead.
    const state = runWithOwner(owner, () => {
      const root = createRoot((dispose) => ({ dispose, state: createKeybind(action) }))
      onCleanup(root.dispose)
      return root.state
    })!
    keybinds.set(action, state)
    return state
  }

  return {
    newChat: {
      behavior: (): NewChatBehavior => newChatBehavior.value() === "first-fork" ? "first-fork" : "new-chat",
      setBehavior: newChatBehavior.set,
    },
    sharing: {
      // Guests of a shared chat see the owner's unsent typing only after the
      // owner turns this on, per device.
      showTyping: () => shareTyping.value() === true,
      setShowTyping: shareTyping.set,
    },
    sidebar: {
      /** The most chats the sidebar's Working list shows on this device. */
      workingLimit: () => {
        const value = workingLimit.value()
        return isWorkingLimit(value) ? value : DEFAULT_WORKING_LIMIT
      },
      setWorkingLimit: workingLimit.set,
    },
    notifications: {
      agent: agent.value, setAgent: agent.set,
      permissions: permissions.value, setPermissions: permissions.set,
      errors: errors.value, setErrors: errors.set,
    },
    keybinds: {
      get: (action: string) => keybind(action).value(),
      set: (action: string, value: string) => keybind(action).set(value),
      reset: (action: string) => keybind(action).set(null),
      resetAll(actions: string[]) {
        for (const action of new Set([...actions, ...keybinds.keys()])) keybind(action).set(null)
      },
    },
  }
}
